import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Heart, Camera, CheckCircle, MessageCircle, Crown, Star, Globe, Award, Sparkles, ChevronRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
export function CorporatePage() {
  return <LanguageProvider data-id="element-1341">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-1342">
        <ScrollProgress data-id="element-1343" />
        <Navbar data-id="element-1344" />
        <main className="w-full" data-id="element-1345">
          <CorporateHero data-id="element-1346" />
          <WhyPartnerSection data-id="element-1347" />
          <TheExperienceSection data-id="element-1348" />
          <SponsorshipTiersSection data-id="element-1349" />
          <AudienceSection data-id="element-1350" />
          <BenefitsSection data-id="element-1351" />
          <CorporateCTA data-id="element-1352" />
        </main>
        <Footer data-id="element-1353" />
      </div>
    </LanguageProvider>;
}
function CorporateHero() {
  return <section className="relative w-full min-h-screen bg-black flex items-center justify-center overflow-hidden" data-id="element-1354">
      <div className="absolute inset-0" data-id="element-1355">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-30" data-id="element-1356" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" data-id="element-1357"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-1358">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} data-id="element-1359">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-400/70 hover:text-amber-400 text-sm tracking-wider uppercase font-light mb-12 transition-colors" data-id="element-1360">
            <ArrowLeft className="w-4 h-4" data-id="element-1361" />
            Back to Life in the Sikh Kingdoms
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-1362">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6 font-light" data-id="element-1363">
            Presented by Akal Steel
          </p>
          <p className="text-amber-400 tracking-[0.3em] text-xs md:text-sm uppercase mb-8 font-light" data-id="element-1364">
            Brand Partnership &amp; Sponsorship
          </p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-8 tracking-tight leading-none" data-id="element-1365">
            CORPORATE
            <br data-id="element-1366" />
            <span className="text-amber-400" data-id="element-1367">PARTNERSHIP</span>
          </h1>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1368"></div>
          <p className="text-xl md:text-2xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-1369">
            Transform Your Next Event Into an Unforgettable Cultural Experience
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-white/60" data-id="element-1370">
          <div className="text-center" data-id="element-1371">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1372">
              Up to
            </p>
            <p className="font-serif text-3xl text-white" data-id="element-1373">120</p>
            <p className="text-xs font-light" data-id="element-1374">Guests Per Evening</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-1375"></div>
          <div className="text-center" data-id="element-1376">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1377">
              Venue
            </p>
            <p className="font-serif text-xl text-white" data-id="element-1378">Illuminarium</p>
            <p className="text-xs font-light" data-id="element-1379">Toronto</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-1380"></div>
          <div className="text-center" data-id="element-1381">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1382">
              Available
            </p>
            <p className="font-serif text-xl text-white" data-id="element-1383">Spring</p>
            <p className="text-xs font-light" data-id="element-1384">2026</p>
          </div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-1385">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/50 to-transparent" data-id="element-1386"></div>
      </motion.div>
    </section>;
}
function WhyPartnerSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1387">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1388"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1389">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1390">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1391">
            The Case for Partnership
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1392">
            WHY <span className="text-amber-400" data-id="element-1393">PARTNER</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1394"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1395">
            Life in the Sikh Kingdoms offers a rare opportunity to align your
            brand with heritage, excellence, and multicultural innovation at one
            of Toronto's most prestigious cultural events.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1396">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1397">
            <Building2 className="w-8 h-8 text-amber-400 mb-4" data-id="element-1398" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1399">
              Brand Alignment
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-1400">
              Associate your brand with heritage, excellence, and multicultural
              innovation. Position yourself as a champion of cultural
              preservation and community investment in one of Canada's most
              dynamic cities.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1401">
            <Crown className="w-8 h-8 text-amber-400 mb-4" data-id="element-1402" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1403">
              Exclusive Access
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-1404">
              Host a private venue for up to 120 guests with the full immersive
              experience — 360° projections, royal Punjabi cuisine, and
              interactive technology. An evening your clients and partners will
              never forget.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1405">
            <Heart className="w-8 h-8 text-amber-400 mb-4" data-id="element-1406" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1407">
              Community Impact
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-1408">
              Align your CSR mandate with cultural preservation and youth
              education. A portion of all proceeds goes directly to the Sikh Art
              and Culture Gallery at the Royal Ontario Museum — a tangible,
              meaningful contribution.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1409">
            <Camera className="w-8 h-8 text-amber-400 mb-4" data-id="element-1410" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1411">
              Media Exposure
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-1412">
              Dignitary presence, press coverage, and premium photo
              opportunities. Events with cultural significance attract ethnic
              media, community papers, and broader press — amplifying your brand
              story organically.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function TheExperienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1413">
      <div className="container mx-auto px-4" data-id="element-1414">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1415">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1416">
            What Your Guests Will Experience
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1417">
            THE <span className="text-amber-400" data-id="element-1418">EXPERIENCE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-1419"></div>
        </motion.div>

        <div className="max-w-6xl mx-auto" data-id="element-1420">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1421">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1422">
              <div className="aspect-video relative overflow-hidden" data-id="element-1423">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" alt="Immersive Experience" className="w-full h-full object-cover" data-id="element-1424" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1425"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-6" data-id="element-1426">
              <p className="text-white/80 text-lg leading-relaxed font-light" data-id="element-1427">
                Step into the court of Maharaja Ranjit Singh, where history
                unfolds not behind glass, but all around you. Through walls that
                transform, floors that transport, and flavors that resurrect a
                golden age.
              </p>

              <div className="border-l-2 border-amber-400/30 pl-6 space-y-4" data-id="element-1428">
                <div className="flex items-start" data-id="element-1429">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-1430">—</span>
                  <span className="text-white/70 font-light" data-id="element-1431">
                    5–7 courses synchronized with immersive projections
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1432">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-1433">—</span>
                  <span className="text-white/70 font-light" data-id="element-1434">
                    360° visual storytelling across walls, floor, and tables
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1435">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-1436">—</span>
                  <span className="text-white/70 font-light" data-id="element-1437">
                    Royal Punjabi cuisine from world-renowned chefs
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1438">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-1439">—</span>
                  <span className="text-white/70 font-light" data-id="element-1440">
                    90 minutes of complete immersion
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1441">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-1442">—</span>
                  <span className="text-white/70 font-light" data-id="element-1443">
                    Interactive technology — voice, motion, and scent
                  </span>
                </div>
              </div>

              <div className="border border-amber-400/30 p-6 bg-black/40" data-id="element-1444">
                <div className="grid grid-cols-3 gap-4 text-center" data-id="element-1445">
                  <div data-id="element-1446">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-1447">
                      Venue
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-1448">
                      Illuminarium Toronto
                    </p>
                  </div>
                  <div data-id="element-1449">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-1450">
                      Date
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-1451">Spring 2026</p>
                  </div>
                  <div data-id="element-1452">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-1453">
                      Capacity
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-1454">
                      Up to 120 Guests
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function SponsorshipTiersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1455">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1456"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1457">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1458">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1459">
            Investment Levels
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1460">
            SPONSORSHIP <span className="text-amber-400" data-id="element-1461">TIERS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1462"></div>
          <p className="text-white/70 font-light max-w-2xl mx-auto" data-id="element-1463">
            Each tier is designed to deliver meaningful brand value while
            supporting cultural preservation and community impact.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1464">
          {/* Presenting Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0
        }} className="relative" data-id="element-1465">
            <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-1466">
              <div className="bg-amber-400 text-black font-bold py-2 px-6 flex items-center gap-2" data-id="element-1467">
                <Star className="w-4 h-4" fill="currentColor" data-id="element-1468" />
                <span className="text-xs uppercase tracking-wider" data-id="element-1469">
                  Flagship
                </span>
              </div>
            </div>
            <div className="border-2 border-amber-400 bg-black/80 p-8 pt-12 h-full flex flex-col" data-id="element-1470">
              <div className="text-center mb-6" data-id="element-1471">
                <Crown className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-1472" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-1473">
                  Presenting Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-1474"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-1475">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1476">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1477"></span>
                  Full venue branding throughout the evening
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1478">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1479"></span>
                  2 Patron Circle tables (20 guests)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1480">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1481"></span>
                  Logo on all event materials &amp; press releases
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1482">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1483"></span>
                  Press conference inclusion &amp; media opportunity
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1484">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1485"></span>
                  VIP reception hosting rights
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1486">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1487"></span>
                  Dedicated social media feature campaign
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1488">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1489"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Presenting%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full bg-amber-400 hover:bg-amber-500 text-black py-3 text-center text-sm tracking-wider uppercase font-medium transition-colors duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1490">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>

          {/* Cultural Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} data-id="element-1491">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-1492">
              <div className="text-center mb-6" data-id="element-1493">
                <Award className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-1494" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-1495">
                  Cultural Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-1496"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-1497">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1498">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1499"></span>
                  1 Patron Circle table (10 guests)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1500">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1501"></span>
                  Logo on select event materials
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1502">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1503"></span>
                  Social media mention &amp; feature
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1504">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1505"></span>
                  Behind-the-scenes preview access
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1506">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1507"></span>
                  Branded photo moment at the event
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1508">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1509"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Cultural%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1510">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>

          {/* Community Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-1511">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-1512">
              <div className="text-center mb-6" data-id="element-1513">
                <Globe className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-1514" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-1515">
                  Community Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-1516"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-1517">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1518">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1519"></span>
                  5 Founder Circle tickets
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1520">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1521"></span>
                  Logo on community-facing materials
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1522">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1523"></span>
                  Newsletter mention &amp; community recognition
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1524">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1525"></span>
                  Youth program sponsorship opportunity
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1526">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1527"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Community%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1528">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function AudienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1529">
      <div className="container mx-auto px-4" data-id="element-1530">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1531">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1532">
            Who You'll Reach
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1533">
            AUDIENCE &amp; <span className="text-amber-400" data-id="element-1534">DEMOGRAPHICS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-1535"></div>
        </motion.div>

        <div className="max-w-5xl mx-auto" data-id="element-1536">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-id="element-1537">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-1538">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-1539">120</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-1540">
                Guests Per Evening
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-1541">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-1542">HNW</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-1543">
                High-Net-Worth Attendees
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-1544">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-1545">50+</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-1546">
                Diaspora Leaders &amp; Professionals
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-1547">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-1548">Press</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-1549">
                Media &amp; Cultural Influencers
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border-2 border-amber-400/40 p-8 md:p-12 bg-gradient-to-br from-amber-950/20 to-black/60 text-center" data-id="element-1550">
            <Heart className="w-8 h-8 text-amber-400 mx-auto mb-4" data-id="element-1551" />
            <p className="text-2xl text-amber-400 font-light italic mb-4" data-id="element-1552">
              "A portion of all proceeds goes directly to the Sikh Art and
              Culture Gallery at the Royal Ontario Museum, preserving Sikh
              heritage for future generations."
            </p>
            <p className="text-white/50 text-sm font-light tracking-wider uppercase" data-id="element-1553">
              Your sponsorship is a tax-deductible charitable contribution
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function BenefitsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1554">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1555"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1556">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1557">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1558">
            Your Investment Includes
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1559">
            WHAT SPONSORS <span className="text-amber-400" data-id="element-1560">RECEIVE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-1561"></div>
        </motion.div>

        <div className="max-w-4xl mx-auto" data-id="element-1562">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1563">
            {['Premium brand visibility throughout the event', 'Tax-deductible charitable donation receipt', 'Professional photography from the evening', 'Press kit inclusion and media distribution', 'Digital content package for social channels', 'Networking with community and diaspora leaders', 'Recognition in event program and credits', 'Access to future partnership opportunities'].map((benefit, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.05
          }} className="flex items-center gap-4 border border-amber-400/20 p-4 bg-black/40" data-id="element-1564">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-1565" />
                <span className="text-white/70 font-light text-sm" data-id="element-1566">
                  {benefit}
                </span>
              </motion.div>)}
          </div>
        </div>
      </div>
    </motion.section>;
}
function CorporateCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1567">
      <div className="container mx-auto px-4" data-id="element-1568">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto text-center" data-id="element-1569">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-6" data-id="element-1570" />
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6" data-id="element-1571">
            Ready to <span className="text-amber-400" data-id="element-1572">Partner?</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1573"></div>
          <p className="text-xl text-white/70 font-light mb-10 leading-relaxed" data-id="element-1574">
            We welcome the opportunity to discuss how a partnership with Life in
            the Sikh Kingdoms can serve your brand, your team, and your
            community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1575">
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20corporate%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-1576">
              <MessageCircle className="w-5 h-5" data-id="element-1577" />
              Inquire via WhatsApp
            </motion.a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border-2 border-amber-400/50 hover:border-amber-400 text-white hover:text-amber-400 px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" data-id="element-1578">
              <ArrowLeft className="w-4 h-4" data-id="element-1579" />
              Back to Main Site
            </Link>
          </div>
          <p className="text-white/40 text-sm font-light mt-6" data-id="element-1580">
            Our team will respond within 24 hours to discuss partnership
            opportunities.
          </p>
        </motion.div>
      </div>
    </motion.section>;
}